import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Zoom,
} from "@mui/material";
import { MediaLibraryDialogView } from "pages/MediaLibraryPage/MediaLibraryDialogView";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { IMediaItem, MediaItemSource } from "services/KnowfullyAdminClient";
import { Color } from "types/colors";
import { MediaType } from "utils/mediaUtils";

const styles = {
  root: {
    maxWidth: 300,
    width: "fit-content",
    margin: "10px 15px 0 15px",
  },
  controls: {
    display: "flex",
    alignItems: "center",
  },
  image: {
    width: 150,
    height: "auto",
  },
};

type MediaLibraryImageSelectorProps = {
  defaultPictureUrl?: string;
  value?: string;
  onChange?: (mediaLibraryItemId?: string) => void;
};

export const MediaLibraryImageSelector = ({
  defaultPictureUrl,
  value,
  onChange,
}: MediaLibraryImageSelectorProps) => {
  const { mediaService } = useGlobalContext();

  const { data: picture, isLoading } = useQuery(
    ["media-item", value],
    () => mediaService.getItem(value!, MediaItemSource.MediaLibrary),
    {
      enabled: !!value,
    }
  );

  const { formatMessage: t } = useIntl();
  const [loaded, setLoaded] = useState(false);
  const [mediaLibraryDialog, setMediaLibraryDialog] = useBoolean(false);

  useEffect(() => {
    if (!mediaLibraryDialog) {
      setLoaded(!isLoading);
    }
  }, [isLoading, mediaLibraryDialog]);

  const onSelectProfileImage = (mediaLibraryItem: IMediaItem) => {
    setMediaLibraryDialog.setFalse();
    onChange && onChange(mediaLibraryItem.id);
  };

  const thumbnail = picture?.data?.fileUrl ?? defaultPictureUrl;

  return (
    <>
      <Card sx={styles.root}>
        <CardContent>
          <div>
            <Zoom in={loaded} timeout={1000}>
              <div>
                <CardMedia
                  component="img"
                  sx={styles.image}
                  image={thumbnail}
                  style={
                    thumbnail ? { border: "1px solid lightgrey" } : undefined
                  }
                  onLoad={() => setLoaded(true)}
                />
              </div>
            </Zoom>
            {!loaded && <CircularProgress />}
          </div>
        </CardContent>
        <CardActions sx={styles.controls}>
          {picture ? (
            <div style={{ display: "flex" }}>
              <Tooltip
                title={t({
                  id: "addPicture",
                })}
                style={{ textAlign: "left" }}
              >
                <IconButton
                  style={{ color: Color.Green }}
                  onClick={() => {
                    setMediaLibraryDialog.setTrue();
                    setLoaded(false);
                  }}
                >
                  <AddPhotoAlternateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={t({
                  id: "removePicture",
                })}
                style={{ textAlign: "left" }}
              >
                <IconButton
                  style={{ color: Color.Red }}
                  onClick={() => {
                    onChange && onChange(undefined);
                  }}
                >
                  <HighlightOffIcon />
                </IconButton>
              </Tooltip>
            </div>
          ) : (
            <Tooltip
              title={t({
                id: "addPicture",
              })}
              style={{ textAlign: "left" }}
            >
              <IconButton
                style={{ color: Color.Green }}
                onClick={() => {
                  setMediaLibraryDialog.setTrue();
                  setLoaded(false);
                }}
              >
                <AddPhotoAlternateIcon />
              </IconButton>
            </Tooltip>
          )}
        </CardActions>
      </Card>
      <Dialog
        fullWidth
        maxWidth={"lg"}
        open={mediaLibraryDialog}
        onClose={setMediaLibraryDialog.setFalse}
      >
        <DialogContent>
          <MediaLibraryDialogView
            onClickMediaLibraryItem={onSelectProfileImage}
            options={[MediaType.IMAGE]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
